import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import BackgroundIcon from '@components/BackgroundIcon';
import ProfileStatus from '@components/CurationBanner/PatientProfileStatus';
import { TableTaskStatus } from '@components/DataTable';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import AddRecordModalContent from '@components/Modal/Content/AddRecordModalContent/AddRecordModalContent';
import RecordSync from '@components/RecordSync';
import RoundedButton from '@components/RoundedButton';
import SkeletonLoader from '@components/SkeletonLoader';
import WelcomeMessage from '@components/WelcomeMessage';

import { CLIENTS } from '@constants/routes';

import { useGetAllCuratorTasksForUser } from '@hooks/api/curator-task';
import { useGetAllPatientUsers } from '@hooks/api/user';
import { useCuration } from '@hooks/useCuration';
import { useIsMobile } from '@hooks/useIsMobile';
import { useModal } from '@hooks/useModal';
import { useSession } from '@hooks/useSession';

import AppLayout from '@layouts/AppLayout';
import { MainContainer, Spacer } from '@layouts/Layouts/BigTitleLayout';

import { TaskPriorityMap } from '@utils/curator-task';
import { formatClientId, formatDate, formatName } from '@utils/format';

import type { CuratorTaskModel } from 'generated/types';
import type { GetAllPatientUsersQuery } from 'generated/user.generated';

import { DashContents, DashboardStyle } from './ClientDashboard.style';

const messages = defineMessages({
  noPatients: {
    id: 'curatorDashboard.noPatients',
    defaultMessage: 'No Patients',
  },
  noTasks: {
    id: 'curatorDashboard.noTasks',
    defaultMessage: 'No Tasks',
  },
  patients: {
    id: 'curatorDashboard.patients',
    defaultMessage: 'My Patients',
  },
  tasks: {
    id: 'curatorDashboard.tasks',
    defaultMessage: 'My Tasks',
  },
  onboardingPatients: {
    id: 'curatorDashboard.onboardingPatients',
    defaultMessage: 'My Onboarding Patients',
  },
  view: {
    id: 'curatorDashboard.view',
    defaultMessage: '{text} Record',
  },
  externalPatient: {
    id: 'organization.patient.status.external',
    defaultMessage: 'External Patient',
  },
});

interface RowData {
  vitall_id: string;
  user_id: string;
  name: string;
  lastLogin?: string | null;
  lastRecordUpdate: string;
  newRecords?: number | null;
  needsReviewRecords?: number | null;
  draftRecords?: number | null;
  publishedRecords?: number | null;
  vitall_patient_id?: string | null;
  fhirID?: string | null;
  patient?:
    GetAllPatientUsersQuery['getAllPatientUsers'][0]['vitall_patient'] | null;
}

function VitallDashboard() {
  const { formatMessage } = useIntl();
  const mobile = useIsMobile();
  const { closeModal, openModal, setContent } = useModal();
  const {
    state: { isCurator, loggedInProfile },
  } = useSession();
  const { viewAsPatient } = useCuration();
  const [first_name, user_id] = [
    loggedInProfile?.first_name,
    loggedInProfile?.user_id,
  ];

  const { data: patientsData, loading: patientsLoading } =
    useGetAllPatientUsers({ user_id });
  const { data: tasksData, loading: tasksLoading } =
    useGetAllCuratorTasksForUser();

  const patientData = (patientsData ?? []).map<RowData>(
    ({
      first_name,
      last_login,
      last_name,
      user_id,
      vitall_patient,
      vitall_user_id,
    }) => ({
      vitall_id: formatClientId(vitall_user_id ?? '', true),
      user_id: user_id,
      name: `${first_name} ${last_name}`,
      lastLogin: last_login,
      // TODO curator.curation_meta.last_record_update make last record update queryable in CurationMeta
      lastRecordUpdate: '-',
      newRecords: vitall_patient?.recordMeta?.New ?? 0,
      needsReviewRecords: vitall_patient?.recordMeta?.NeedsReview ?? 0,
      draftRecords: vitall_patient?.recordMeta?.Draft ?? 0,
      publishedRecords: vitall_patient?.recordMeta?.Published ?? 0,
      vitall_patient_id: vitall_patient?.vitall_patient_id,
      fhirID: vitall_patient?.patient_id,
      patient: vitall_patient,
    })
  );

  const taskData = (tasksData?.data ?? []).map((task) => {
    const {
      created_at,
      curator_task_id,
      description,
      due_date,
      name,
      priority,
      status,
      vitall_patient,
      vitall_patient_id,
    } = task;

    const clientName = vitall_patient?.user
      ? formatName(
          vitall_patient.user.first_name,
          vitall_patient.user.last_name
        )
      : '-';

    return {
      id: curator_task_id,
      dateCreated: created_at,
      name,
      client: clientName,
      description,
      dueDate: formatDate({ date: due_date }),
      priority,
      status,
      vitall_patient_id,
      task,
    };
  });

  const onboardingPatientsData: {
    name: string;
    registrationStep: string;
    date: string;
  }[] = [];

  const handleOpenModal = (user_id: string) => {
    openModal({
      width: 500,
      props: {
        height: '300px',
      },
      children: null,
    });
    setContent({
      children: (
        <AddRecordModalContent user_id={user_id} onClose={closeModal} />
      ),
    });
  };

  return (
    <AppLayout loading={patientsLoading || tasksLoading}>
      <BackgroundIcon />
      <DashboardStyle>
        {first_name ? <WelcomeMessage name={first_name} /> : null}
        <DashContents />
        {!mobile && <RecordSync />}
      </DashboardStyle>

      <MainContainer>
        <SkeletonLoader loading={patientsLoading} height={400} width="100%">
          <MuiDataTable
            columns={[
              {
                name: 'vitall_id',
                className: 'small',
                label: 'ID',
              },
              {
                name: 'name',
                className: 'blue-hover',
                label: 'Client Name',
                options: {
                  searchable: true,
                  sort: true,
                },
              },
              {
                name: 'lastRecordUpdate',
                className: 'date',
                label: 'Last Record Update',
                options: {
                  filter: true,
                  sort: true,
                },
              },
              {
                name: 'lastLogin',
                className: 'date',
                label: 'Last Login Date',
                options: {
                  filter: true,
                  sort: true,
                  customBodyRender: (lastLogin?: string | null) =>
                    lastLogin
                      ? formatDate({ date: lastLogin, formatter: 'datetime' })
                      : '-',
                },
                desktopOnly: true,
              },
              {
                name: 'newRecords',
                label: 'New Records',
                desktopOnly: true,
                options: {
                  customBodyRender: (value: number) => value,
                  filter: true,
                  sort: true,
                },
              },
              {
                name: 'needsReviewRecords',
                label: 'Needs Review Records',
                desktopOnly: true,
                options: {
                  customBodyRender: (value: number) => value,
                  filter: true,
                  sort: true,
                },
              },
              {
                name: 'draftRecords',
                label: 'Draft Records',
                desktopOnly: true,
                options: {
                  customBodyRender: (value: number) => value,
                  filter: true,
                  sort: true,
                },
              },
              {
                name: 'publishedRecords',
                label: 'Published Records',
                desktopOnly: true,
                options: {
                  customBodyRender: (value: number) => value,
                  filter: true,
                  sort: true,
                },
              },
              {
                name: 'patient',
                label: 'Status',
                options: {
                  customBodyRender: (value: RowData['patient']) =>
                    value?.status ? (
                      <ProfileStatus
                        read_only
                        status={value.status}
                        variant="secondary"
                        vitall_patient_id={value.vitall_patient_id}
                      />
                    ) : (
                      '-'
                    ),
                },
              },
              {
                name: 'vitall_patient_id',
                options: {
                  customHeadLabelRender: () => null,
                  customBodyRender: (vitall_patient_id: string) => (
                    <RoundedButton
                      // eslint-disable-next-line @typescript-eslint/no-misused-promises
                      onClick={async (e) => {
                        e.stopPropagation();
                        handleOpenModal(vitall_patient_id);
                      }}
                    >
                      {`${formatMessage(messages.view, {
                        text: 'Add',
                      })}`}
                    </RoundedButton>
                  ),
                },
              },
            ]}
            data={patientData}
            options={{
              onRowClick: async (_rowData, { dataIndex }) => {
                if (dataIndex <= patientData.length - 1) {
                  const row = patientData[dataIndex];
                  const { user_id } = row;
                  await viewAsPatient(user_id);
                  navigate(`${CLIENTS}${user_id}`);
                }
              },
              search: true,
              sort: true,
              filter: true,
              sortOrder: {
                name: 'name',
                direction: 'asc',
              },
              textLabels: {
                body: {
                  noMatch: formatMessage(messages.noPatients),
                },
              },
            }}
            title={formatMessage(messages.patients)}
          />
        </SkeletonLoader>

        <Spacer />
        {isCurator ? (
          <SkeletonLoader loading={tasksLoading} height={400} width="100%">
            <MuiDataTable
              columns={[
                {
                  name: 'task',
                  options: {
                    customHeadLabelRender: () => null,
                    customBodyRender: (task: CuratorTaskModel) => (
                      <TableTaskStatus task={task} />
                    ),
                  },
                },
                {
                  name: 'name',
                  className: 'small',
                  label: 'Task Name',
                  options: {
                    filter: true,
                    sort: true,
                    searchable: true,
                  },
                },
                {
                  name: 'client',
                  className: 'blue-hover',
                  label: 'Client',
                  options: {
                    filter: true,
                    sort: true,
                    searchable: true,
                  },
                },
                {
                  name: 'description',
                  className: 'small-light',
                  label: 'Description',
                  options: {
                    searchable: true,
                  },
                  desktopOnly: true,
                },
                {
                  name: 'dueDate',
                  className: 'date',
                  label: 'Due Date',
                  options: {
                    filter: true,
                    sort: true,
                  },
                },
                {
                  name: 'priority',
                  label: 'Priority',
                  className: 'small',
                  options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value: number) =>
                      TaskPriorityMap[value].text,
                  },
                  desktopOnly: true,
                },
              ]}
              data={taskData}
              options={{
                search: true,
                sort: true,
                filter: true,
                sortOrder: {
                  name: 'dueDate',
                  direction: 'asc',
                },
                textLabels: {
                  body: {
                    noMatch: formatMessage(messages.noTasks),
                  },
                },
              }}
              title={formatMessage(messages.tasks)}
            />
            <Spacer />
            <MuiDataTable
              columns={[
                {
                  name: 'name',
                  className: 'blue-hover',
                  label: 'Client Name',
                  options: {
                    filter: true,
                    searchable: true,
                    sort: true,
                  },
                },
                {
                  name: 'registrationStep',
                  className: 'small',
                  label: 'Registration Step',
                  options: {
                    filter: true,
                    searchable: true,
                    sort: true,
                  },
                },
                {
                  name: 'date',
                  className: 'date',
                  label: 'Date',
                  options: {
                    filter: true,
                    sort: true,
                  },
                },
              ]}
              data={onboardingPatientsData}
              options={{
                search: true,
                sort: true,
                filter: true,
                sortOrder: {
                  name: 'name',
                  direction: 'asc',
                },
                textLabels: {
                  body: {
                    noMatch: formatMessage(messages.noPatients),
                  },
                },
              }}
              title={formatMessage(messages.onboardingPatients)}
            />
          </SkeletonLoader>
        ) : null}
      </MainContainer>
    </AppLayout>
  );
}

export default VitallDashboard;
